import React, { useState, useEffect } from 'react';
import SearchBar from '../../components/SearchBar';
import ProductCard from '../../components/ProductCard';
import EmptyState from '../../components/EmptyState';
import IngredientDrawer from '../../components/IngredientDrawer';
import { supabase } from '../../lib/supabase';
import { fetchSavedProductIds, saveProduct, unsaveProduct } from '../../lib/api/saved-products';

function SearchPage({ onNavigate }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [savedIds, setSavedIds] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);

  useEffect(() => {
    async function loadSaved() {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        const ids = await fetchSavedProductIds(user.id);
        setSavedIds(ids);
      }
    }
    loadSaved();
  }, []);

  async function handleSearch(searchQuery) {
    const trimmed = searchQuery.trim();
    if (!trimmed) return;

    setLoading(true);
    setSearched(true);
    setResults([]);

    const { data, error } = await supabase
      .from('products')
      .select('*')
      .or(`name.ilike.%${trimmed}%,brand.ilike.%${trimmed}%,description.ilike.%${trimmed}%`);

    setResults(error ? [] : data ?? []);
    setLoading(false);
  }

  function handleSuggestionSelect(suggestion) {
    setQuery(suggestion);
    handleSearch(suggestion);
  }

  async function handleSave(product) {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    if (savedIds.has(product.id)) {
      await unsaveProduct(user.id, product.id);
      setSavedIds((prev) => { const next = new Set(prev); next.delete(product.id); return next; });
    } else {
      await saveProduct(user.id, product.id);
      setSavedIds((prev) => new Set(prev).add(product.id));
    }
  }

  return (
    <div className="px-6 py-10">
      <div className="max-w-content mx-auto">
        <div className="flex items-center gap-3 mb-2">
          <button
            type="button"
            onClick={() => onNavigate('home')}
            aria-label="Back to browse"
            className="p-1 text-neutral-500 hover:text-primary-500 transition-colors duration-fast focus:outline-none focus:ring-2 focus:ring-primary-500 rounded-md"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M19 12H5M12 5l-7 7 7 7" />
            </svg>
          </button>
          <h1 className="text-h2 text-neutral-900">Search Products</h1>
        </div>
        <p className="text-body text-neutral-500 mb-8">
          Search by product name, brand, or ingredient keyword.
        </p>

        <div className="mb-10">
          <SearchBar
            value={query}
            onChange={setQuery}
            onSubmit={handleSearch}
            onSuggestionSelect={handleSuggestionSelect}
            isLoading={loading}
          />
        </div>

        {loading && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="bg-surface-card rounded-lg border border-neutral-200 p-6 flex flex-col gap-4">
                <div className="flex justify-between gap-3">
                  <div className="flex flex-col gap-2 flex-1">
                    <div className="h-3 bg-neutral-100 rounded-md animate-pulse w-1/3" />
                    <div className="h-5 bg-neutral-100 rounded-md animate-pulse w-2/3" />
                  </div>
                  <div className="h-6 w-16 bg-neutral-100 rounded-sm animate-pulse" />
                </div>
                <div className="h-5 w-24 bg-neutral-100 rounded-full animate-pulse" />
                <div className="space-y-2">
                  <div className="h-3 bg-neutral-100 rounded-md animate-pulse" />
                  <div className="h-3 bg-neutral-100 rounded-md animate-pulse w-4/5" />
                </div>
              </div>
            ))}
          </div>
        )}

        {!loading && searched && results.length > 0 && (
          <>
            <p className="text-small text-neutral-500 mb-6">
              {results.length} {results.length === 1 ? 'result' : 'results'} for &ldquo;{query}&rdquo;
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {results.map((product) => (
                <ProductCard
                  key={product.id}
                  product={product}
                  saved={savedIds.has(product.id)}
                  onSave={handleSave}
                  onClick={() => setSelectedProduct(product)}
                />
              ))}
            </div>
          </>
        )}

        {!loading && searched && results.length === 0 && (
          <EmptyState
            title="No results found"
            description={`No products matched "${query}". Try a different keyword or brand name.`}
          />
        )}

        {!searched && (
          <EmptyState
            title="Search for a product"
            description={'Type a product name, brand, or keyword like \u201cfragrance-free\u201d or \u201cdish soap.\u201d'}
          />
        )}
      </div>

      {selectedProduct && (
        <IngredientDrawer
          product={selectedProduct}
          onClose={() => setSelectedProduct(null)}
        />
      )}
    </div>
  );
}

export default SearchPage;
